<template>
  <div class="collection-detail">
    <!-- 返回按钮 -->
    <div class="back-button">
      <el-button @click="goBack" icon="el-icon-arrow-left">返回</el-button>
    </div>

    <!-- 分类标题 -->
    <div class="collection-header">
      <h1><t-text :text="collectionTitle"></t-text></h1>
    </div>

    <!-- 加载中状态 -->
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="6" animated />
    </div>

    <!-- 错误提示 -->
    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      style="margin: 20px 0;"
    />

    <!-- 鞋子列表 -->
    <div v-if="!loading && !error" class="shoes-container">
      <!-- 空数据提示 -->
      <el-empty
        v-if="shoes.length === 0"
        description="暫無鞋款"
      >
        <el-button @click="goToAllShoes">瀏覽所有鞋款</el-button>
      </el-empty>

      <!-- 鞋子网格 -->
      <div v-else class="shoes-grid">
        <router-link 
          v-for="shoe in shoes" 
          :key="shoe.id"
          :to="`/shoes/${shoe.id}`"
          class="shoe-card"
          custom
          v-slot="{ navigate }"
        >
          <div class="shoe-card-content" @click="navigate" role="link" tabindex="0" @keyup.enter="navigate">
            <div class="shoe-image">
              <!-- 使用内联SVG作为默认显示，优化首屏渲染速度 -->
              <!-- <div class="placeholder-image-container">
                <svg width="100%" height="100%" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                  <rect width="200" height="200" fill="#f5f5f5"/>
                  <text x="50%" y="50%" fill="#aaa" text-anchor="middle" dominant-baseline="middle">{{shoe.title}}</text>
                </svg>
              </div> -->
              
              <!-- 仅使用背景图片加载实际图片，不会阻塞渲染和交互 -->
              <!-- <div 
                v-if="shoe.images && shoe.images.length > 0"
                class="real-image"
                :style="{ backgroundImage: `url(${getShoeImage(shoe)})` }"
              ></div> -->
              <div class="related-image">
                <img :src="getShoeImage(shoe)" :alt="shoe.title" />
              </div>
              
              <!-- 添加点击指示器 -->
              <div class="click-indicator">
                <span>查看詳情</span>
              </div>
            </div>
            <div class="shoe-info">
              <h3><t-text :text="shoe.title"></t-text></h3>
              <p class="model-number" v-if="shoe.model_number"><t-text :text="shoe.model_number"></t-text></p>
              <p class="model-number" v-else>無型號</p>
            </div>
          </div>
        </router-link>
      </div>

      <!-- 加载更多按钮 -->
      <div v-if="shoes.length > 0" class="load-more">
        <div v-if="noMoreData" class="no-more-data">
          <t-text text="沒有更多鞋子了"></t-text>
        </div>
        <el-button
          v-else
          :disabled="loading || noMoreData"
          @click="loadMore"
        >
          <template #icon v-if="loading">
            <el-icon class="is-loading"><el-icon-loading /></el-icon>
          </template>
          <t-text :text="loading ? '加載中...' : '加載更多'"></t-text>
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'

export default {
  name: 'CollectionDetailView',
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    
    // 响应式状态
    const collectionTitle = ref('')
    const currentPage = ref(1)
    const pageSize = ref(20)
    const previousTotal = ref(0)  // 追踪上一次加载的数据量
    const noMoreData = ref(false) // 标记是否还有更多数据
    
    // 计算属性
    const shoes = computed(() => store.getters['shoes/allShoes'])
    const collections = computed(() => store.getters['shoes/allCollections'])
    const loading = computed(() => store.getters['shoes/isLoading'])
    const error = computed(() => store.getters['shoes/errorMessage'])
    const totalCount = computed(() => store.getters['shoes/totalShoesCount'])

    const backendBaseUrl = process.env.VUE_APP_BACKEND_BASE_URL;
    // 获取分类标题
    const updateCollectionTitle = () => {
      const collectionId = route.params.id
      const collection = collections.value.find(c => c.id === collectionId)

      // collectionTitle.value = collection ? collection.title : '暫無資料'
      collectionTitle.value = route.params.id
    }
    
    // 获取分类鞋子列表
    const fetchShoes = async (refresh = true) => {
      const collectionId = route.params.id
      
      // 更新筛选条件
      if (refresh) {
        await store.dispatch('shoes/updateFilters', {
          collection: collectionId,
          search: ''
        })
        
        currentPage.value = 1
        previousTotal.value = 0
        noMoreData.value = false
      }
      
      // 获取鞋子数据
      const result = await store.dispatch('shoes/fetchShoes', { 
        page: currentPage.value, 
        limit: pageSize.value, 
        refresh: refresh 
      })
      
      // 更新是否有更多数据
      // 如果当前页数据量少于每页大小，或当前页无数据，则标记没有更多数据
      if (result.length < pageSize.value || result.length === 0) {
        noMoreData.value = true
      }
      
      // 记录当前数据量
      previousTotal.value = shoes.value.length
      
      // 更新分类标题
      updateCollectionTitle()
    }
    
    // 加载更多鞋子
    const loadMore = async () => {
      if (noMoreData.value || loading.value) return
      
      currentPage.value++
      await fetchShoes(false)
      
      // 如果加载后数据量没有增加，说明没有更多数据了
      if (shoes.value.length === previousTotal.value) {
        noMoreData.value = true
      }
    }
    
    // 获取鞋子图片
    const getShoeImage = (shoe) => {
      if (shoe.mainImage) {
        // 返回图片数组中的最后一张图片（ID 最小的）
        return `${backendBaseUrl}/${shoe.mainImage}`
      }
      return 'https://via.placeholder.com/300x300?text=No+Image'
    }
    
    // 跳转到鞋子详情
    const goToShoe = (shoeId) => {
      router.push(`/shoes/${shoeId}`)
    }
    
    // 跳转到所有鞋子页面
    const goToAllShoes = () => {
      router.push('/shoes')
    }
    
    // 返回上一页
    const goBack = () => {
      router.push('/collections')
    }
    
    // 监听路由参数变化
    watch(() => route.params.id, (newId, oldId) => {
      if (newId && newId !== oldId) {
        fetchShoes(true)
      }
    })
    
    // 组件挂载时执行
    onMounted(async () => {
      // 确保有分类数据
      if (collections.value.length === 0) {
        await store.dispatch('shoes/fetchCollections')
      }
      
      // 获取鞋子数据
      fetchShoes(true)
    })
    
    return {
      collectionTitle,
      shoes,
      loading,
      error,
      totalCount,
      loadMore,
      getShoeImage,
      goToShoe,
      goToAllShoes,
      goBack,
      noMoreData
    }
  }
}
</script>

<style scoped>
.collection-detail {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.back-button {
  margin-bottom: 20px;
}

.collection-header {
  text-align: center;
  margin-bottom: 30px;
}

.collection-header h1 {
  font-size: 2.5rem;
  color: #333;
  margin: 0;
}

.loading-container {
  margin: 40px 0;
}

.shoes-container {
  margin-top: 20px;
}

.shoes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.shoe-card {
  text-decoration: none;
  color: inherit;
  display: block;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  cursor: pointer;
  position: relative; /* 确保相对定位 */
  user-select: none; /* 防止文本被选中影响体验 */
  outline: none; /* 移除默認的焦點輪廓 */
}

.shoe-card-content {
  display: block;
  height: 100%;
  width: 100%;
}

.shoe-card:hover, .shoe-card:focus {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.shoe-card:active {
  transform: translateY(0);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: transform 0.1s, box-shadow 0.1s;
}

/* .shoe-image {
  height: 200px;
  position: relative;
  overflow: hidden;
  background-color: #f5f5f5;
} */

.placeholder-image-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.real-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  z-index: 2;
  transition: transform 0.3s ease;
}

.shoe-card:hover .real-image {
  transform: scale(1.05);
}

/* 添加點擊指示器樣式 */
.click-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  text-align: center;
  padding: 8px 0;
  transform: translateY(100%);
  transition: transform 0.3s ease;
  z-index: 3;
}

.shoe-card:hover .click-indicator {
  transform: translateY(0);
}

.shoe-info {
  padding: 15px;
}

.shoe-info h3 {
  margin: 0 0 10px 0;
  font-size: 18px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.related-image {
  height: 180px;
  overflow: hidden;
}

.related-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.related-card:hover .related-image img {
  transform: scale(1.05);
}

.model-number {
  color: #888;
  font-size: 14px;
  margin: 0;
}

.load-more {
  text-align: center;
  margin-top: 40px;
}

.no-more-data {
  text-align: center;
  color: #909399;
  font-size: 14px;
  margin: 20px 0;
}

@media (max-width: 768px) {
  .shoes-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .shoes-grid {
    grid-template-columns: 1fr;
  }
}
</style> 